import React from 'react';
import type { ArtistProfile } from 'ts-core';
import { UserAvatar } from '../UserAvatar';
import { SocialChips, MediaSections } from './mediaTiles';

export interface ProfileDetailPanelProps {
    profile: ArtistProfile;
    /** Optional content rendered at the bottom (e.g. a mobile close button). */
    footerSlot?: React.ReactNode;
}

/**
 * Presentational, reusable public-profile pane content: header (avatar, name,
 * events count + joined year), social links, bio, photo grid and video section.
 * The slide-in shell + close affordance are the caller's job. Shares its media
 * tiles + social chips with PartnerDetailPanel via ./mediaTiles.
 */
export const ProfileDetailPanel: React.FC<ProfileDetailPanelProps> = ({ profile, footerSlot }) => {
    const metaParts: string[] = [];
    if (typeof profile.eventsCount === 'number') {
        metaParts.push(`${profile.eventsCount} ${profile.eventsCount === 1 ? 'event' : 'events'}`);
    }
    if (profile.joinedYear) {
        metaParts.push(`Joined ${profile.joinedYear}`);
    }

    return (
        <div className="profile-detail-panel p-6 space-y-6">
            {/* Header */}
            <header className="flex items-center gap-4">
                <UserAvatar
                    name={profile.name}
                    avatar={profile.avatar}
                    media={profile.media}
                    size={80}
                    className="ring-2 ring-gray-100 flex-shrink-0"
                />
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-gray-900 truncate">{profile.name}</h1>
                    {metaParts.length > 0 && (
                        <p className="text-sm text-gray-500 mt-1">{metaParts.join(' · ')}</p>
                    )}
                </div>
            </header>

            <SocialChips links={profile.socialLinks ?? []} />

            {profile.bio && (
                <p className="text-gray-700 whitespace-pre-line">{profile.bio}</p>
            )}

            <MediaSections media={profile.media ?? []} title={profile.name || 'media'} />

            {footerSlot}
        </div>
    );
};

export default ProfileDetailPanel;
